import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Lock, Globe, Star, Archive, RotateCcw, Link2 } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import { LiveDataReciprocal } from "@/components/LiveDataLinks";
import { EntReciprocal } from "@/components/EntReciprocal";

const TYPES = ["web", "document", "interview", "dataset", "report", "social"];
const CULTURAL_TYPES = ["community_elder_testimony", "traditional_ruler_council", "cultural_custodian", "oral_historian",
  "family_lineage_account", "recorded_oral_history", "archival_document", "colonial_administrative_record", "missionary_record",
  "government_record", "museum_catalog", "archaeological_report", "academic_book", "peer_reviewed_article", "linguistic_study",
  "newspaper_media_report", "audio_recording", "video_recording", "photograph", "artifact_inscription", "personal_observation",
  "diaspora_community_testimony"];
const DOC_CRED = ["", "unassessed", "low", "medium", "high"];
const CULT_AUTH = ["", "unknown", "external_observer", "scholar", "descendant", "community_member", "direct_custodian"];
const prettyT = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Sources() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [tab, setTab] = useState("active"); // active | archived
  const [action, setAction] = useState(null); // { mode: 'archive'|'restore', source }
  const [busy, setBusy] = useState(false);
  const [ldFor, setLdFor] = useState(null); // source shown in Live Data reciprocal modal

  function blank() {
    return { title: "", url: "", publisher: "", type: "web", credibility_rating: 3, is_private: false, internal_notes: "", project_ids: [], documentary_credibility: "", cultural_authority: "" };
  }

  const loadSources = useCallback(() => {
    const params = tab === "archived" ? { archived: true } : {};
    api.get("/sources", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [tab]);
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadSources(); }, [loadSources]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/sources", { ...form, credibility_rating: Number(form.credibility_rating) });
      toast.success("Source added");
      setOpen(false);
      setForm(blank());
      loadSources();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleProject = (pid) => {
    setForm((f) => ({
      ...f,
      project_ids: f.project_ids.includes(pid) ? f.project_ids.filter((x) => x !== pid) : [...f.project_ids, pid],
    }));
  };

  const runAction = async () => {
    if (busy || !action) return;
    setBusy(true);
    const { mode, source } = action;
    try {
      if (mode === "archive") await api.delete(`/sources/${source.id}`);
      else await api.post(`/sources/${source.id}/restore`);
      toast.success(mode === "archive" ? "Source archived" : "Source restored");
      setAction(null);
      loadSources();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6">
      <PageHeader title="Source Library" subtitle="Sources can belong to many projects (many-to-many).">
        <Btn data-testid="new-source-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Add Source</Btn>
      </PageHeader>

      <div className="flex items-center gap-4 border-b border-zinc-800 mb-4">
        {["active", "archived"].map((t) => (
          <button key={t} data-testid={`source-tab-${t}`} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-sm font-semibold border-b-2 transition-colors capitalize ${tab === t ? "border-emerald-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            {t}
          </button>
        ))}
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm" data-testid="sources-table">
          <thead>
            <tr className="text-left label-caps text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-2.5">Title</th>
              <th className="px-4 py-2.5">Publisher</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Cred.</th>
              <th className="px-4 py-2.5">Visibility</th>
              <th className="px-4 py-2.5">Projects</th>
              <th className="px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-zinc-600" data-testid="sources-empty">{tab === "archived" ? "No archived sources." : "No sources yet."}</td></tr>}
            {items.map((s) => (
              <tr key={s.id} className="hover:bg-zinc-900/50" data-testid={`source-row-${s.id}`}>
                <td className="px-4 py-2.5 font-medium">
                  {s.url ? <a href={s.url} target="_blank" rel="noreferrer" className="hover:underline">{s.title}</a> : s.title}
                </td>
                <td className="px-4 py-2.5 text-zinc-400">{s.publisher || "—"}</td>
                <td className="px-4 py-2.5 text-zinc-400">{s.type}</td>
                <td className="px-4 py-2.5 text-amber-400 flex items-center gap-0.5"><Star className="h-3 w-3 fill-amber-400" /> {s.credibility_rating || "—"}</td>
                <td className="px-4 py-2.5">
                  {s.is_private ? (
                    <span className="inline-flex items-center gap-1 text-red-300 text-xs"><Lock className="h-3 w-3" /> Private</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-emerald-300 text-xs"><Globe className="h-3 w-3" /> Public</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-zinc-500 font-mono-data text-xs">{s.project_ids?.length || 0}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  {tab === "active" ? (
                    <button data-testid={`source-archive-${s.id}`} onClick={() => setAction({ mode: "archive", source: s })}
                      className="inline-flex items-center gap-1.5 rounded-sm border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-colors"><Archive className="h-3.5 w-3.5" /> Archive</button>
                  ) : (
                    <button data-testid={`source-restore-${s.id}`} onClick={() => setAction({ mode: "restore", source: s })}
                      className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/20 hover:text-emerald-200 transition-colors"><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
                  )}
                  <button data-testid={`source-livedata-${s.id}`} onClick={() => setLdFor(s)}
                    className="ml-2 inline-flex items-center gap-1.5 rounded-sm border border-zinc-700 px-2.5 py-1 text-xs font-medium text-zinc-300 hover:bg-zinc-800 transition-colors"><Link2 className="h-3.5 w-3.5" /> Live Data</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={!!action} onClose={() => { if (!busy) setAction(null); }} title={action?.mode === "archive" ? "Archive source" : "Restore source"}>
        {action && (
          <div className="space-y-4" data-testid="source-action-confirm">
            <p className="text-sm text-zinc-400">
              {action.mode === "archive"
                ? "Archive this source? It is hidden from the active library but preserved with all metadata and project/claim/intelligence relationships intact, and can be restored anytime. Linked claims, evidence and projects are not deleted."
                : "Restore this source to the active library? All metadata and existing relationships are preserved."}
            </p>
            <div className="text-sm font-mono-data break-all" data-testid="source-action-title">{action.source.title}</div>
            <div className="flex gap-2">
              <Btn variant="ghost" data-testid="source-action-cancel" disabled={busy} onClick={() => setAction(null)}>Cancel</Btn>
              <Btn data-testid="source-action-confirm-btn" disabled={busy} onClick={runAction} className="flex-1 justify-center">
                {busy ? "Working…" : action.mode === "archive" ? "Archive" : "Restore"}
              </Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!ldFor} onClose={() => setLdFor(null)} title="Linked Sports Live Data">
        {ldFor && (
          <div className="space-y-2" data-testid="source-livedata-modal">
            <p className="text-xs text-zinc-500 break-all">{ldFor.title}</p>
            <LiveDataReciprocal kind="source" id={ldFor.id} />
            <EntReciprocal kind="source" id={ldFor.id} />
          </div>
        )}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Source" wide>
        <form onSubmit={create} className="space-y-4">
          <Field label="Title"><input data-testid="source-title-input" className={inputCls} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="URL"><input className={inputCls} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></Field>
            <Field label="Publisher"><input className={inputCls} value={form.publisher} onChange={(e) => setForm({ ...form, publisher: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select data-testid="source-type" className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <optgroup label="General">{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</optgroup>
                <optgroup label="Cultural & Heritage">{CULTURAL_TYPES.map((t) => <option key={t} value={t}>{prettyT(t)}</option>)}</optgroup>
              </select>
            </Field>
            <Field label="Credibility (1-5)"><input type="number" min={1} max={5} className={inputCls} value={form.credibility_rating} onChange={(e) => setForm({ ...form, credibility_rating: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Documentary credibility">
              <select data-testid="source-doc-credibility" className={inputCls} value={form.documentary_credibility} onChange={(e) => setForm({ ...form, documentary_credibility: e.target.value })}>{DOC_CRED.map((t) => <option key={t} value={t}>{t ? prettyT(t) : "—"}</option>)}</select>
            </Field>
            <Field label="Cultural authority / proximity">
              <select data-testid="source-cultural-authority" className={inputCls} value={form.cultural_authority} onChange={(e) => setForm({ ...form, cultural_authority: e.target.value })}>{CULT_AUTH.map((t) => <option key={t} value={t}>{t ? prettyT(t) : "—"}</option>)}</select>
            </Field>
          </div>
          <div className="text-[0.65rem] text-zinc-500 border border-zinc-800 rounded-sm p-2" data-testid="source-cultural-note">
            Documentary credibility and cultural authority are <b className="text-amber-300">not the same</b>. Written evidence is not automatically superior to oral testimony; colonial and missionary records may carry institutional bias; and one individual does not automatically represent an entire community.
          </div>
          <Field label="Internal Notes (never public)">
            <textarea className={inputCls} rows={2} value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input data-testid="source-private-toggle" type="checkbox" checked={form.is_private} onChange={(e) => setForm({ ...form, is_private: e.target.checked })} />
            Private source (excluded from published output)
          </label>
          <Field label="Link to Projects">
            <div className="flex flex-wrap gap-2">
              {projects.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggleProject(p.id)}
                  className={`text-xs px-2.5 py-1 rounded-sm border ${form.project_ids.includes(p.id) ? "bg-emerald-600 border-emerald-500 text-white" : "border-zinc-700 text-zinc-400"}`}
                >
                  {p.title}
                </button>
              ))}
              {projects.length === 0 && <span className="text-xs text-zinc-600">No projects yet.</span>}
            </div>
          </Field>
          <Btn data-testid="source-save-btn" type="submit" className="w-full justify-center">Save Source</Btn>
        </form>
      </Modal>
    </div>
  );
}
